import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import apiClient from '../api/client';
import { useToast } from '../components/ui/Toast';
import { Skeleton } from '../components/ui/Skeleton';
import { Input } from '../components/ui/Input';
import { Select } from '../components/ui/Select';
import { BackButton } from '../components/ui/BackButton';
import { StatusPill, PaymentPill } from './OrdersList';
import { usePermission, useAnyPermission } from '../auth/usePermission';
import { ShelfLocations } from '../components/warehouse/ShelfLocations';
import { Permission } from '../auth/permissions';
import { formatDate } from '../lib/format';
import { enumLabel } from '../lib/enumLabel';
import { describeApiError } from '../lib/errorMessages';
import { FileText, Pencil } from 'lucide-react';
import { formatEUR } from '../lib/format';
import { PrintDocumentDialog } from '../components/PrintDocumentDialog';
import { OrderDocuments } from '../components/order/OrderDocuments';
import { OrderPayments } from '../components/order/OrderPayments';
import { OrderCreditPanel } from '../components/order/OrderCreditPanel';
import { OrderHistoryDialog } from '../components/receiving/OrderHistoryDialog';
import { CancelLineDialog } from '../components/receiving/BinsBoard';
import {
  HandoverWizard,
  Journey,
  PrimaryAction,
  StatusStrip,
  sheetFigures,
} from '../components/order/OrderSheet';

interface OrderLine {
  id: string;
  productId: string | null; // null on a SHIPPING line (no product)
  lineType?: 'GOODS' | 'SHIPPING';
  productSku?: string | null;
  productName?: string | null;
  productBrand?: string | null;
  manufacturerRef?: string | null;
  /** The barcode the hand-over scans (order sheet, step 2). */
  productEan?: string | null;
  productEan2?: string | null;
  /** Where to pick from: the main shelf location, or the order's bin for a received supplier part. */
  location?: string | null;
  /** SHELF lines: every shelf location of the part, main first (migration 144). */
  locations?: string[] | null;
  /** fn_order_line_location kind (migration 112): SHELF | SUPPLIER | ORDER_BIN. */
  locationKind?: 'SHELF' | 'SUPPLIER' | 'ORDER_BIN' | null;
  /** SUPPLIER lines: when the goods are expected. */
  expectedAt?: string | null;
  /** 3.12 — an external line no supplier order carries: nothing is coming until someone orders it. */
  notOrdered?: boolean;
  quantity: number;
  /** WP-3 — cumulative quantity already handed over across shipments. */
  quantityDelivered?: number;
  /** WP-4 §8c — units the supplier never shipped. `quantity` stays the ordered figure. */
  quantityShort?: number;
  /** Units already asked back on a non-rejected return of this order (SC-08). */
  quantityReturned?: number;
  /** Supplier units already arrived — what a per-part cancel can no longer take back. */
  quantityReceived?: number;
  unitPriceHT: string | number;
  discountPct: string | number;
  lineTotalHT: string | number;
  source: 'INTERNAL' | 'EXTERNAL';
  status: string;
}
/** WP-4 — the supplier order(s) fired for this order's EXTERNAL lines. */
interface OrderSupplierPo {
  id: string;
  poNumber: string;
  status: string;
  supplierName: string;
  supplierOrderRef: string | null;
  sendAttempts: number;
  lastSendError: string | null;
  nextRetryAt: string | null;
  escalatedAt: string | null;
  closedShort: boolean;
  brfNumber: string | null;
  qtyOrdered: number;
  qtyReceived: number;
}

interface OrderDetailDto {
  id: string;
  orderNumber?: string;
  status: string;
  /** WP-3 §8b — COMPLETE ships in one go; PARTIAL ships what's ready and lets the rest follow. */
  deliveryMode?: 'PARTIAL' | 'COMPLETE';
  /** The order's bin for received supplier parts (migration 112); null = the order number. */
  binLocation?: string | null;
  /** E7 — the four digits created with the order; E8 asks for them at the collection desk. */
  pickupCode?: string | null;
  /** Who the order is for — company name, falling back to the contact name. */
  customerName?: string | null;
  customerRef?: string | null;
  lines?: OrderLine[];
  /** Derived from the payment ledger server-side — not the fulfilment status. */
  paymentStatus?: string;
  totalTTC?: number;
  /** The three figures totalTTC is made of, so the shipping charge can be shown and edited. */
  subtotalHT?: number;
  shippingHT?: number;
  vatAmount?: number;
  amountPaid?: number;
  amountDue?: number;
  pickingLocations?: string[];
  /** What must be paid before the supplier is ordered (backend order/prepayment.ts). */
  prepayment?: {
    onAccount: boolean;
    hasSupplierLine: boolean;
    totalTTC: number;
    /** A payment (any amount, full or partial) must be recorded before the supplier is ordered. */
    paymentRequired: boolean;
    paid: number;
    /** What is left to pay — due at delivery. */
    balanceDue: number;
    satisfied: boolean;
  } | null;
  /** 2026-10-30 — validation refused because the supplier did not reserve every unit. */
  supplierBlock?: { reason: string | null; blockedAt: string } | null;
  purchasePriceHT?: number;
  marginPct?: number;
}

/**
 * Shipping is part of composing the order, so it stays editable exactly as long as a discount
 * does (Invariant #17). Past preparation the figure is on its way to a customer document and a
 * correction is a credit note, not an edit — the server enforces the same set.
 */
const SHIPPING_EDITABLE = ['CREATED', 'PENDING_APPROVAL', 'CONFIRMED'];

/* SC-08 — what can still come back: GOODS units that actually LEFT, less whatever a return
   already claims. Derived from the LINES, never from the order status, because that is the real
   condition: a PARTIALLY_DELIVERED order has returnable units, a READY one does not, and a fully
   returned DELIVERED order has nothing left to offer. The delivery charge is an order_line too
   and there is no such thing as returning it, so SHIPPING is excluded. */
interface Returnable {
  productId: string;
  sku: string | null;
  name: string | null;
  delivered: number;
  max: number;
}
function returnableFrom(lines: OrderLine[] | undefined): Returnable[] {
  /* Grouped PER PRODUCT, not per line. One reference can sit on two lines of the same order —
     the units we had on the shelf and the ones a supplier sent — and `quantityReturned` is
     already a product-level figure, so two rows would each offer the whole allowance and let the
     desk ask back twice what is owed. */
  const byProduct = new Map<string, Returnable>();
  for (const l of lines ?? []) {
    if (l.lineType === 'SHIPPING' || !l.productId) continue;
    const cur = byProduct.get(l.productId);
    const delivered = (cur?.delivered ?? 0) + (l.quantityDelivered ?? 0);
    byProduct.set(l.productId, {
      productId: l.productId,
      sku: l.productSku ?? cur?.sku ?? null,
      name: l.productName ?? cur?.name ?? null,
      delivered,
      max: delivered - (l.quantityReturned ?? 0),
    });
  }
  return [...byProduct.values()].filter((r) => r.max > 0);
}

/**
 * ORDER_CANCEL reaches every pre-delivery state. Once anything was handed over the server refuses
 * a cancellation (ALREADY_HANDED_OVER — that is a return), so the button is not offered then.
 */
const CANCELLABLE = ['CREATED', 'PENDING_APPROVAL', 'CONFIRMED', 'PREPARING', 'READY'];

/** Screen 22 — Order Detail (projection per role; actions per state). */
export default function OrderDetail() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id = '' } = useParams();
  const { showApiError, push } = useToast();
  const [order, setOrder] = useState<OrderDetailDto | null>(null);
  const canDeliver = usePermission(Permission.ORDER_DELIVER);
  const canPrepare = usePermission(Permission.ORDER_PREPARE);
  const canReady = usePermission(Permission.ORDER_READY);
  // Manager / owner only (spec; feedback 2.4) — the B2B portal has no cancel.
  const canCancelAll = usePermission(Permission.ORDER_CANCEL);
  const canCancel = canCancelAll;
  const canConfirm = usePermission(Permission.ORDER_CONFIRM);
  const canPay = usePermission(Permission.ORDER_PAY);
  /* Feedback 3.2 — an account customer's prepayment is accounting's act (OWNER, ACCOUNTING), not
     the counter's; the server refuses ORDER_PAY on /prepay since 2026-10-27. */
  const canReconcile = usePermission(Permission.PAYMENT_RECONCILE);
  const canApprove = usePermission(Permission.ORDER_APPROVE_CREDIT);
  /* Feedback 6.6 — a B2B comptant customer pays its own order by card online (CAWL). The server
     says whether this order qualifies and for how much (GET /orders/:id/checkout); the button
     only appears when it does, so the portal never offers a payment the POST would refuse. */
  const canPayOnline = usePermission(Permission.ORDER_PAY_ONLINE);
  const [cardOffer, setCardOffer] = useState<{ offered: boolean; amount: number } | null>(null);
  const [cardBusy, setCardBusy] = useState(false);
  // WP-1 DISCOUNT_APPLY — MANAGER is capped at managerMaxDiscountPct, OWNER clears the cap
  // and the below-cost floor. The server enforces it; this just exposes the action.
  const canApplyDiscount = usePermission(Permission.DISCOUNT_APPLY_DIRECT);
  const [showDiscount, setShowDiscount] = useState(false);
  const [discountPct, setDiscountPct] = useState('');
  const [discountReason, setDiscountReason] = useState('');
  const [applyingDiscount, setApplyingDiscount] = useState(false);
  // SC-08 — the return desk. Opened from the order so the form can only offer what this order
  // actually delivered; the free-form /returns console cannot know that.
  const [returning, setReturning] = useState(false);
  const [returnQty, setReturnQty] = useState<Record<string, string>>({});
  const [returnReason, setReturnReason] = useState('');
  const [returnBusy, setReturnBusy] = useState(false);
  // E8 — the hand-over: identify, scan out of the bin, collect the balance, done.
  const [wizard, setWizard] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  // Cancel ONE supplier part not yet received (receiving spec §6, /order-lines/:id/cancel).
  const [cancelLine, setCancelLine] = useState<{
    lineId: string;
    productName: string;
    orderNumber: string;
    notYet: number;
  } | null>(null);
  // What a cash customer owes for the parts that are here now (server figure, INV-05).
  const [quote, setQuote] = useState<{
    onAccount: boolean;
    /** Billed periodically: money settles the invoice in Règlements, never the order (3.2). */
    periodicBilling?: boolean;
    collectibleNowTTC: number;
    awaitedTTC: number;
  } | null>(null);
  // B2B prepay (decision §5): an on-account garage pays ahead for its supplier parts → DEP.
  const [prepaying, setPrepaying] = useState(false);
  const [prepayMethod, setPrepayMethod] = useState('TRANSFER');
  const [prepayAmount, setPrepayAmount] = useState('');
  const [prepayRef, setPrepayRef] = useState('');
  const [prepayBusy, setPrepayBusy] = useState(false);
  const [editingShipping, setEditingShipping] = useState(false);
  const [shippingDraft, setShippingDraft] = useState('');
  const [savingShipping, setSavingShipping] = useState(false);
  // WP-4 — an EXTERNAL line reads AWAITING_SUPPLIER whether its PO went out or is stuck retrying
  // after a supplier-API failure. Purchasing/warehouse staff get the actual reason here; the
  // fetch is gated on the same permission as the endpoint so nobody else triggers a 403.
  const canSeeSupplierPos = useAnyPermission(Permission.PO_PLACE, Permission.STOCK_RECEIVE);
  const [supplierPos, setSupplierPos] = useState<OrderSupplierPo[]>([]);
  // Bumped on every reload so the BL panel re-fetches — a new shipment's BL must appear
  // immediately after the delivery, not on the next page load.
  const [version, setVersion] = useState(0);

  const reload = async () => {
    try {
      const { data } = await apiClient.get<OrderDetailDto>(`/orders/${id}`);
      setOrder(data);
      setVersion((v) => v + 1);
      apiClient
        .get<{
          onAccount: boolean;
          periodicBilling?: boolean;
          collectibleNowTTC: number;
          awaitedTTC: number;
        }>(`/orders/${id}/payment-quote`)
        .then((r) => setQuote(r.data))
        .catch(() => setQuote(null));
    } catch (err) {
      showApiError(err);
    }
  };
  useEffect(() => {
    void reload();
  }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!canPayOnline || !id) return;
    apiClient
      .get<{ offered: boolean; amount: number }>(`/orders/${id}/checkout`)
      .then((r) => setCardOffer(r.data))
      .catch(() => setCardOffer(null));
  }, [id, canPayOnline, version]);

  /** Off to CAWL's hosted page; it sends the customer back to /b2b/orders, which asks the server
   *  for the outcome (OrdersList) — coming back is never taken as proof of payment. */
  const payByCard = async () => {
    setCardBusy(true);
    try {
      const { data } = await apiClient.post<{ checkoutUrl: string }>(
        `/orders/${id}/checkout`,
        {},
        { headers: { 'X-Command-Id': crypto.randomUUID() } }
      );
      window.location.href = data.checkoutUrl;
    } catch (err) {
      showApiError(err);
      setCardBusy(false);
    }
  };

  useEffect(() => {
    if (!canSeeSupplierPos || !id) return;
    void (async () => {
      try {
        const { data } = await apiClient.get<OrderSupplierPo[]>(`/purchasing/orders?orderId=${id}`);
        setSupplierPos(data);
      } catch {
        // Purely informational panel — never let it break the order screen.
      }
    })();
  }, [id, canSeeSupplierPos, version]);

  const [cancelling, setCancelling] = useState(false);
  const [cancelReason, setCancelReason] = useState('');
  const [cancelBusy, setCancelBusy] = useState(false);

  const action = async (path: string, body: unknown = {}) => {
    try {
      await apiClient.post(`/orders/${id}/${path}`, body, {
        headers: { 'X-Command-Id': crypto.randomUUID() },
      });
      push({ tone: 'success', message: t('orders.detail.actionOk', { action: path }) });
      await reload();
    } catch (err) {
      showApiError(err);
    }
  };

  /**
   * SC-08 — raise the return REQUEST. It moves nothing: stock, the encours and the credit note
   * all wait for RETURN_RECEIVE, which is the warehouse's act on the /returns desk once the
   * parts are physically back. Approval sits between the two, and this screen deliberately does
   * not offer it — the avoir queue wants a requester who is not the approver.
   */
  const submitReturn = async () => {
    const lines = returnableFrom(order?.lines)
      .map((r) => ({ ...r, qty: Number(returnQty[r.productId] ?? 0) }))
      .filter((r) => r.qty > 0);
    if (lines.length === 0) {
      push({ tone: 'warning', message: t('orders.detail.return.pickSomething') });
      return;
    }
    const over = lines.find((r) => !Number.isInteger(r.qty) || r.qty > r.max);
    if (over) {
      push({
        tone: 'warning',
        message: t('orders.detail.return.tooMany', { sku: over.sku ?? '', max: over.max }),
      });
      return;
    }
    setReturnBusy(true);
    try {
      const { data } = await apiClient.post<{ id: string }>(
        '/returns',
        {
          orderId: id,
          reason: returnReason.trim() || undefined,
          lines: lines.map((r) => ({ productId: r.productId, quantity: r.qty })),
        },
        { headers: { 'X-Command-Id': crypto.randomUUID() } }
      );
      push({ tone: 'success', message: t('orders.detail.return.created', { id: data.id }) });
      setReturning(false);
      setReturnQty({});
      setReturnReason('');
      // Reload so the lines carry the units this request just claimed — asking for the same
      // part twice in a row must not offer it again as if nothing had happened.
      await reload();
    } catch (err) {
      showApiError(err);
    } finally {
      setReturnBusy(false);
    }
  };

  const submitPrepay = async () => {
    const amount = Number(prepayAmount);
    if (!Number.isFinite(amount) || amount <= 0) {
      push({ tone: 'warning', message: t('orders.detail.payment.amountPositive') });
      return;
    }
    setPrepayBusy(true);
    try {
      const { data } = await apiClient.post<{
        depositInvoice?: { invoiceNumber: string } | null;
        exposureReleased: number;
      }>(
        `/orders/${id}/prepay`,
        {
          method: prepayMethod,
          amount,
          ...(prepayRef.trim() ? { reference: prepayRef.trim() } : {}),
        },
        { headers: { 'X-Command-Id': crypto.randomUUID() } }
      );
      push({
        tone: 'success',
        message: t('orders.sheet.prepay.done', {
          number: data.depositInvoice?.invoiceNumber ?? '',
          released: formatEUR(data.exposureReleased ?? 0),
        }),
      });
      setPrepaying(false);
      setPrepayRef('');
      await reload();
    } catch (err) {
      showApiError(err);
    } finally {
      setPrepayBusy(false);
    }
  };

  const saveShipping = async (amountHT: number | null) => {
    setSavingShipping(true);
    try {
      await apiClient.post(
        `/orders/${id}/shipping`,
        { amountHT },
        { headers: { 'X-Command-Id': crypto.randomUUID() } }
      );
      push({
        tone: 'success',
        message: amountHT
          ? t('orders.detail.shippingSet', { amount: formatEUR(amountHT) })
          : t('orders.detail.shippingRemoved'),
      });
      setEditingShipping(false);
      await reload();
    } catch (err) {
      showApiError(err);
    } finally {
      setSavingShipping(false);
    }
  };

  const applyDiscount = async () => {
    const pct = Number(discountPct);
    if (!Number.isFinite(pct) || pct <= 0 || pct > 100) {
      push({ tone: 'warning', message: t('orders.detail.discountRange') });
      return;
    }
    setApplyingDiscount(true);
    try {
      const { data } = await apiClient.post<{
        alert: boolean;
        dailyTotalHT: number;
        thresholdHT: number;
      }>(`/orders/${id}/apply-discount`, { pct, reason: discountReason });
      push({ tone: 'success', message: t('orders.detail.discountApplied', { pct }) });
      if (data.alert) {
        push({
          tone: 'warning',
          message: t('orders.detail.discountDailyAlert', {
            total: formatEUR(data.dailyTotalHT),
            threshold: formatEUR(data.thresholdHT),
          }),
        });
      }
      setShowDiscount(false);
      setDiscountPct('');
      setDiscountReason('');
      await reload();
    } catch (err) {
      showApiError(err);
    } finally {
      setApplyingDiscount(false);
    }
  };

  if (!order) return <Skeleton className="h-40 w-full" />;

  const figures = sheetFigures(order.lines);
  // The hand-over walks a confirmed order the rest of the way itself (OrderService.handover).
  const canHandOver =
    canDeliver && ['CONFIRMED', 'PREPARING', 'READY', 'PARTIALLY_DELIVERED'].includes(order.status);
  // How the order ships is derived from its lines, never chosen (2026-09-18): in stock and from a
  // supplier → two hand-overs (PARTIAL); one origin → one hand-over. Shown, not editable.
  const partialMode = order.deliveryMode === 'PARTIAL';
  // WP-4 §8c — a supplier shortfall closed part of this order and released the reservation on
  // what never came. The exposure moved without anyone touching the order, so the order itself
  // has to say so: the extra column and the banner below only appear when it actually happened.
  const shortLines = (order.lines ?? []).filter((l) => (l.quantityShort ?? 0) > 0);
  const hasShortfall = shortLines.length > 0;
  const returnableLines = returnableFrom(order.lines);

  return (
    <div className="space-y-4">
      <BackButton label={t('orders.detail.backToOrders')} className="mb-0" />
      <header className="flex flex-wrap items-center gap-3 border-b pb-4">
        <h1 className="text-xl font-semibold tracking-tight">
          {t('orders.detail.heading', { number: order.orderNumber ?? order.id.slice(0, 8) })}
        </h1>
        <StatusPill s={order.status} />
        {order.supplierBlock && (
          <span className="text-xs px-2 py-0.5 rounded bg-destructive/10 text-destructive font-semibold">
            {t('orders.list.supplierBlocked')}
          </span>
        )}
        <PaymentPill status={order.paymentStatus} amountDue={order.amountDue} />
        {order.deliveryMode && (
          <span
            className="text-xs px-2 py-0.5 rounded border text-muted-foreground"
            title={partialMode ? t('orders.detail.partialTitle') : t('orders.detail.completeTitle')}
          >
            {partialMode ? t('orders.detail.partialDelivery') : t('orders.detail.completeDelivery')}
          </span>
        )}
        {/* The order's internal log: scans, closes, decisions, anomalies, customer messages. */}
        <button
          type="button"
          onClick={() => setHistoryOpen(true)}
          className="ml-auto text-sm text-primary hover:underline"
        >
          {t('orders.sheet.history')}
        </button>
      </header>
      {/* 2026-10-30 — the owner's 100 % rule: the supplier did not reserve every unit (or did not
          answer), so this order was never validated and never will be. No payment, no hand-over,
          no manual override: the buyer has the supplier's number to cancel it through our contact
          or keep the goods; a manager cancels the order; the basket is placed again if the sale
          stands. */}
      {order.supplierBlock && (
        <div
          className="rounded border border-destructive/40 bg-destructive/5 px-4 py-3 text-sm"
          role="alert"
        >
          <div className="font-semibold text-destructive">
            {t('orders.detail.supplierBlockedTitle')}
          </div>
          <div className="mt-1">{order.supplierBlock.reason}</div>
          <div className="mt-1 text-muted-foreground">{t('orders.detail.supplierBlockedHint')}</div>
        </div>
      )}
      {historyOpen && (
        <OrderHistoryDialog
          orderId={id}
          orderNumber={order.orderNumber ?? null}
          onClose={() => setHistoryOpen(false)}
        />
      )}
      {cancelLine && (
        <CancelLineDialog
          line={cancelLine}
          onClose={() => setCancelLine(null)}
          onDone={(text) => {
            setCancelLine(null);
            push({ tone: 'success', message: text });
            void reload();
          }}
        />
      )}
      {/* Whose order this is. First line under the heading because at a counter it is the first
          thing anyone checks, and `customerRef` below is the customer's own reference for the
          job (their PO number, a plate) — it never said who they were. */}
      {order.customerName && (
        <p className="text-sm">
          <span className="text-muted-foreground">{t('orders.detail.customer')}</span>{' '}
          <span className="font-medium text-foreground">{order.customerName}</span>
        </p>
      )}
      {order.customerRef && (
        <p className="text-sm text-muted-foreground">
          {t('orders.detail.yourReference', { ref: order.customerRef })}
        </p>
      )}
      {/* E7 — the four digits the customer was given at the confirmation and again when the
          order was ready. The counter reads them back at the collection desk (E8). */}
      {order.pickupCode && (
        <p className="text-sm text-muted-foreground">
          {t('orders.detail.pickupCode')}{' '}
          <span className="font-mono tracking-widest text-foreground">{order.pickupCode}</span>
        </p>
      )}
      {/* Settlement summary — makes "delivered but not paid" unambiguous at the counter. */}
      {order.paymentStatus && (
        <p className="text-sm text-muted-foreground">
          {t('orders.detail.paidOf', {
            paid: formatEUR(Number(order.amountPaid ?? 0)),
            total: formatEUR(Number(order.totalTTC ?? 0)),
          })}
          {Number(order.amountDue ?? 0) > 0 && (
            <span className="font-medium text-foreground">
              {' '}
              · {t('orders.detail.stillDue', { amount: formatEUR(Number(order.amountDue)) })}
            </span>
          )}
        </p>
      )}

      {/* Order sheet: where the parts are, and the one thing to do next. */}
      {order.lines && order.lines.some((l) => l.productId) && order.status !== 'CANCELLED' && (
        <div className="space-y-3">
          <StatusStrip f={figures} />
          <PrimaryAction
            f={figures}
            canHandOver={canHandOver}
            balance={quote && !quote.onAccount ? quote.collectibleNowTTC : null}
            onHandOver={() => setWizard(true)}
          />
        </div>
      )}
      {wizard && (
        <HandoverWizard
          orderId={id}
          orderNumber={order.orderNumber ?? ''}
          lines={order.lines ?? []}
          collection={order.status === 'READY' || order.status === 'PARTIALLY_DELIVERED'}
          onClose={() => setWizard(false)}
          onDone={() => {
            setWizard(false);
            void reload();
          }}
        />
      )}

      {order.lines && order.lines.length > 0 && (
        <section className="border rounded p-4 bg-card">
          <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
            <h2 className="font-semibold">{t('common.lines')}</h2>
            {/* Received supplier parts go straight into the ORDER's bin — one per order, so the
                parts of one order are kept together. Only meaningful with supplier lines. */}
            {order.lines.some((l) => l.source === 'EXTERNAL' && l.lineType !== 'SHIPPING') &&
              !['DELIVERED', 'CANCELLED'].includes(order.status) && (
                <OrderBinInput
                  orderId={id}
                  orderNumber={order.orderNumber ?? ''}
                  bin={order.binLocation ?? null}
                  editable={canPrepare || canReady}
                  onSaved={reload}
                />
              )}
          </div>
          <table className="w-full text-sm">
            <thead className="text-muted-foreground">
              <tr>
                <th className="text-left py-1">{t('common.product')}</th>
                <th className="text-left">{t('orders.detail.location')}</th>
                <th>{t('common.qty')}</th>
                <th>{t('orders.detail.delivered')}</th>
                {hasShortfall && (
                  <th title={t('orders.detail.shortTitle')}>{t('orders.detail.short')}</th>
                )}
                <th>{t('common.ht')}</th>
                <th>{t('orders.detail.disc')}</th>
                <th>{t('common.totalHT')}</th>
                <th>{t('orders.detail.source')}</th>
                <th>{t('common.status')}</th>
              </tr>
            </thead>
            <tbody>
              {order.lines.map((l) => (
                <tr key={l.id} className="border-t">
                  <td className="py-1">
                    <div className="max-w-[22rem]">
                      <div
                        className="font-medium truncate"
                        title={
                          l.lineType === 'SHIPPING'
                            ? t('orders.detail.shippingLine')
                            : (l.productName ?? l.productId ?? '')
                        }
                      >
                        {l.lineType === 'SHIPPING'
                          ? t('orders.detail.shippingLine')
                          : (l.productName ?? l.productId)}
                      </div>
                      <div className="text-xs text-muted-foreground flex flex-wrap gap-x-2">
                        {l.productSku && <span className="font-mono">{l.productSku}</span>}
                        {l.productBrand && <span>{l.productBrand}</span>}
                        {l.manufacturerRef && (
                          <span>{t('orders.detail.ref', { ref: l.manufacturerRef })}</span>
                        )}
                      </div>
                      <Journey l={l} />
                      {/* A supplier part not arrived yet can be cancelled on its own (customer
                          consent + reason, server-enforced); one already here is a return. */}
                      {canCancel &&
                        l.source === 'EXTERNAL' &&
                        l.productId &&
                        ![
                          'DELIVERED',
                          'CANCELLED',
                          'CLOSED_SHORT',
                          'NOT_FOUND',
                          'CONVERTED',
                          'RECEIVED',
                          'PICKED',
                        ].includes(l.status) &&
                        l.quantity - (l.quantityShort ?? 0) - (l.quantityReceived ?? 0) > 0 && (
                          <button
                            type="button"
                            className="mt-0.5 text-xs text-destructive hover:underline"
                            onClick={() =>
                              setCancelLine({
                                lineId: l.id,
                                productName: l.productName ?? '',
                                orderNumber: order.orderNumber ?? '',
                                notYet:
                                  l.quantity - (l.quantityShort ?? 0) - (l.quantityReceived ?? 0),
                              })
                            }
                          >
                            {t('orders.sheet.cancelPart')}
                          </button>
                        )}
                    </div>
                  </td>
                  <td className="font-mono text-xs">
                    {l.lineType === 'SHIPPING' ? null : l.locationKind === 'SUPPLIER' ? (
                      // Not received: no shelf to send anyone to — only when it is due.
                      <span className="font-sans text-warning">
                        {l.expectedAt
                          ? t('orders.detail.supplierExpected', { date: formatDate(l.expectedAt) })
                          : t('orders.detail.supplierExpectedTbc')}
                      </span>
                    ) : l.locationKind === 'SHELF' && l.productId ? (
                      // Main location first; "shelf empty" for whoever just took the last part
                      // from one of several shelves.
                      <ShelfLocations
                        productId={l.productId}
                        locations={l.locations ?? []}
                        canEmpty={canDeliver && !['DELIVERED', 'CANCELLED'].includes(l.status)}
                        onChange={() => void reload()}
                      />
                    ) : l.location ? (
                      // ORDER_BIN: a received supplier part, or an in-stock part the warehouse
                      // scanned into the customer's bin (migration 162).
                      <span className="font-semibold">
                        {t('orders.detail.inBin', { bin: l.location })}
                      </span>
                    ) : (
                      <span className="text-muted-foreground">—</span>
                    )}
                  </td>
                  <td className="text-center">{l.quantity}</td>
                  <td className="text-center">
                    {(l.quantityDelivered ?? 0) > 0 ? (
                      <span
                        className={
                          (l.quantityDelivered ?? 0) < l.quantity ? 'text-warning font-medium' : ''
                        }
                      >
                        {l.quantityDelivered}
                      </span>
                    ) : (
                      <span className="text-muted-foreground">—</span>
                    )}
                  </td>
                  {hasShortfall && (
                    <td className="text-center">
                      {(l.quantityShort ?? 0) > 0 ? (
                        <span className="text-warning font-medium">{l.quantityShort}</span>
                      ) : (
                        <span className="text-muted-foreground">—</span>
                      )}
                    </td>
                  )}
                  <td className="text-center">{Number(l.unitPriceHT).toFixed(2)}</td>
                  <td className="text-center">{Number(l.discountPct ?? 0).toFixed(2)}%</td>
                  <td className="text-center">{Number(l.lineTotalHT).toFixed(2)}</td>
                  <td className="text-center">
                    <span
                      className={`text-xs px-1.5 py-0.5 rounded ${l.source === 'INTERNAL' ? 'bg-success/15' : 'bg-warning/20'}`}
                    >
                      {l.source}
                    </span>
                  </td>
                  <td className="text-center text-xs">
                    {enumLabel('lineStatus', l.status)}
                    {/* 3.12 — "awaiting supplier" only when a supplier was actually asked. A
                        line the sourcing could not place says so, in red; an order still held
                        for its deposit says that instead (its sourcing has not been asked yet). */}
                    {l.status === 'AWAITING_SUPPLIER' && l.notOrdered ? (
                      <div className="text-[11px] font-semibold text-destructive">
                        {order.supplierBlock
                          ? t('orders.detail.notOrderedBlocked')
                          : order.status === 'CREATED'
                            ? t('orders.detail.reservedAtValidation')
                            : t('orders.detail.notOrdered')}
                      </div>
                    ) : l.status === 'AWAITING_SUPPLIER' ? (
                      <div className="text-[11px] text-warning">
                        {t('orders.detail.awaitingSupplier')}
                      </div>
                    ) : null}
                    {l.status === 'CLOSED_SHORT' && (
                      <div className="text-[11px] text-warning">
                        {t('orders.detail.closedShort')}
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      )}

      {/* Totals — and the one figure on them a seller can still move. */}
      {order.totalTTC != null && (
        <section className="border rounded p-4 bg-card">
          <table className="ml-auto text-sm">
            <tbody>
              <tr>
                <td className="pr-6 py-0.5 text-muted-foreground">{t('common.totalHT')}</td>
                <td className="text-right font-mono">{formatEUR(Number(order.subtotalHT ?? 0))}</td>
              </tr>
              <tr>
                <td className="pr-6 py-0.5 text-muted-foreground">{t('orders.detail.shipping')}</td>
                <td className="text-right font-mono">
                  {editingShipping ? (
                    <span className="inline-flex items-center gap-1">
                      <input
                        autoFocus
                        inputMode="decimal"
                        value={shippingDraft}
                        onChange={(e) => setShippingDraft(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') void saveShipping(Number(shippingDraft) || 0);
                          if (e.key === 'Escape') setEditingShipping(false);
                        }}
                        className="h-7 w-20 rounded-md border border-input bg-background px-1.5 text-right"
                      />
                      <button
                        disabled={savingShipping}
                        onClick={() => void saveShipping(Number(shippingDraft) || 0)}
                        className="rounded bg-primary px-2 py-0.5 text-xs font-medium text-primary-foreground hover:bg-primary/90"
                      >
                        {t('common.save')}
                      </button>
                      <button
                        onClick={() => setEditingShipping(false)}
                        className="rounded border px-1.5 py-0.5 text-xs hover:bg-accent"
                      >
                        {t('common.cancel')}
                      </button>
                    </span>
                  ) : (
                    <>
                      {canConfirm && SHIPPING_EDITABLE.includes(order.status) && (
                        <span className="mr-2 inline-flex gap-1">
                          <button
                            onClick={() => {
                              setShippingDraft(Number(order.shippingHT ?? 0).toFixed(2));
                              setEditingShipping(true);
                            }}
                            className="inline-flex items-center gap-1 rounded bg-primary px-2 py-0.5 text-xs font-medium text-primary-foreground shadow-sm hover:bg-primary/90"
                          >
                            <Pencil className="h-3 w-3" />
                            {t('common.edit')}
                          </button>
                          {Number(order.shippingHT ?? 0) > 0 && (
                            <button
                              disabled={savingShipping}
                              onClick={() => void saveShipping(null)}
                              className="rounded border px-1.5 py-0.5 text-xs text-destructive hover:bg-accent"
                            >
                              {t('common.remove')}
                            </button>
                          )}
                        </span>
                      )}
                      {formatEUR(Number(order.shippingHT ?? 0))}
                    </>
                  )}
                </td>
              </tr>
              <tr>
                <td className="pr-6 py-0.5 text-muted-foreground">{t('common.vat')}</td>
                <td className="text-right font-mono">{formatEUR(Number(order.vatAmount ?? 0))}</td>
              </tr>
              <tr className="border-t">
                <td className="pr-6 py-1 font-medium">{t('common.totalTTC')}</td>
                <td className="text-right font-mono font-semibold">
                  {formatEUR(Number(order.totalTTC))}
                </td>
              </tr>
            </tbody>
          </table>
        </section>
      )}

      {supplierPos.length > 0 && (
        <section className="border rounded p-4 bg-card">
          <h2 className="font-semibold mb-2">{t('orders.detail.supplierPos')}</h2>
          <table className="w-full text-sm">
            <thead className="text-muted-foreground">
              <tr>
                <th className="text-left py-1">{t('orders.detail.po')}</th>
                <th className="text-left">{t('common.supplier')}</th>
                <th className="text-left">{t('orders.detail.poState')}</th>
                <th className="text-center">{t('orders.detail.poReceived')}</th>
              </tr>
            </thead>
            <tbody>
              {supplierPos.map((p) => (
                <tr key={p.id} className="border-t">
                  <td className="py-1 font-mono">
                    {p.poNumber}
                    {p.supplierOrderRef && (
                      <div className="text-xs text-muted-foreground">
                        {t('orders.detail.poRef', { ref: p.supplierOrderRef })}
                      </div>
                    )}
                  </td>
                  <td>{p.supplierName}</td>
                  <td>
                    {enumLabel('poStatus', p.status)}
                    {p.status === 'PENDING' && (
                      <div
                        className={`text-xs ${p.escalatedAt ? 'text-destructive font-semibold' : 'text-warning'}`}
                      >
                        {p.lastSendError === 'MANUAL_CHANNEL'
                          ? t('orders.detail.poManualChannel')
                          : p.escalatedAt
                            ? t('orders.detail.poEscalated', { attempts: p.sendAttempts })
                            : p.sendAttempts > 0
                              ? t('orders.detail.poRetrying', { attempts: p.sendAttempts })
                              : t('orders.detail.poPendingSend')}
                      </div>
                    )}
                    {p.closedShort && (
                      <div className="text-xs text-warning">{t('orders.detail.poClosedShort')}</div>
                    )}
                    {p.brfNumber && (
                      <div className="text-xs text-muted-foreground">
                        {t('orders.detail.poBrf', { number: p.brfNumber })}
                      </div>
                    )}
                  </td>
                  <td className="text-center tabular-nums">
                    {p.qtyReceived}/{p.qtyOrdered}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <p className="text-xs text-muted-foreground mt-2">{t('orders.detail.poFootnote')}</p>
          {hasShortfall && (
            <p className="text-xs text-warning mt-2 border-l-2 border-warning pl-2">
              {t('orders.detail.shortfallNote', { count: shortLines.length })}
            </p>
          )}
        </section>
      )}

      {order.pickingLocations && (
        <section className="border rounded p-4 bg-card">
          <h2 className="font-semibold mb-2">{t('orders.detail.pickingLocations')}</h2>
          <ul className="text-xs font-mono">
            {order.pickingLocations.map((p, i) => (
              <li key={i}>{p}</li>
            ))}
          </ul>
        </section>
      )}

      {/* Every document of the order, printable and e-mailable from one card. */}
      <OrderDocuments orderId={id} refreshKey={version} />

      {/* Cash sales: deposit / settlement receipts, and the invoice handed over on request. */}
      <Receipts orderId={id} refreshKey={version} />

      {/* WP-11 — goods held for collection and the term they are held for (art. 1657). */}
      <PickupSlips
        orderId={id}
        refreshKey={version}
        canResolve={canCancelAll}
        onResolved={reload}
      />

      {/* Cash customer: a supplier part is ordered once a payment is recorded — the full amount
          or a partial upfront payment (a fixed amount the employee chooses); the rest is the
          balance due at delivery. Said on the order, because nothing else explains why a
          confirmed order has no PO yet, or what the customer still owes when they come back. */}
      {order.prepayment &&
        !order.prepayment.onAccount &&
        (order.prepayment.paymentRequired ||
          (order.prepayment.paid > 0 && order.prepayment.balanceDue > 0)) &&
        !['CANCELLED', 'DELIVERED'].includes(order.status) && (
          <section
            className={`border rounded p-4 ${
              order.prepayment.satisfied ? 'bg-card' : 'border-warning/60 bg-warning/10'
            }`}
          >
            <h2 className="font-semibold mb-1">{t('orders.detail.deposit.title')}</h2>
            <p className="text-sm">
              {t('orders.detail.deposit.paidBalance', {
                paid: formatEUR(order.prepayment.paid),
                balance: formatEUR(order.prepayment.balanceDue),
              })}
            </p>
            {order.prepayment.paymentRequired && (
              <p className="text-xs text-muted-foreground mt-1">
                {order.prepayment.satisfied
                  ? t('orders.detail.deposit.met')
                  : ['CREATED', 'PENDING_APPROVAL', 'FROZEN'].includes(order.status)
                    ? t('orders.detail.deposit.confirmFirst')
                    : t('orders.detail.deposit.poHeld')}
              </p>
            )}
          </section>
        )}

      {/* Money comes after the stock check and confirmation, and never beyond what is due —
          the server refuses both (ORDER_NOT_PAYABLE / OVERPAYMENT); this just stops offering it. */}
      <OrderPayments orderId={id} refreshKey={version} />
      <OrderCreditPanel orderId={id} refreshKey={version} />
      {/* Feedback 3.2 (CRITICAL) — a customer billed periodically never pays on the order: the
          money settles his invoice in Recouvrement › Règlements, by accounting. The server
          refuses it here (PERIODIC_BILLING_NOT_AT_COUNTER); this just says where it goes. */}
      {canPay && quote?.periodicBilling && Number(order.amountDue ?? 0) > 0 && (
        <section className="border rounded p-4 bg-card text-sm">
          <h2 className="font-semibold mb-1">{t('orders.detail.payment.title')}</h2>
          <p className="text-muted-foreground">{t('orders.detail.payment.periodicNote')}</p>
          {canReconcile && (
            <Link to="/collection/payments" className="mt-2 inline-block underline">
              {t('orders.detail.payment.periodicLink')}
            </Link>
          )}
        </section>
      )}
      {canPay &&
        !quote?.periodicBilling &&
        !['CREATED', 'PENDING_APPROVAL', 'FROZEN', 'CANCELLED'].includes(order.status) &&
        Number(order.amountDue ?? 0) > 0 && (
          <RecordPayment
            orderId={id}
            amountDue={Number(order.amountDue ?? 0)}
            onRecorded={reload}
          />
        )}

      <section className="border rounded p-4 bg-card">
        <h2 className="font-semibold mb-2">{t('orders.detail.actions')}</h2>
        <div className="flex flex-wrap gap-2 text-sm">
          {cardOffer?.offered && (
            <button
              onClick={() => void payByCard()}
              disabled={cardBusy}
              className="bg-primary text-primary-foreground rounded px-3 py-1 disabled:opacity-60"
            >
              {cardBusy
                ? t('orders.detail.payByCardRedirecting')
                : t('orders.detail.payByCard', { amount: formatEUR(cardOffer.amount) })}
            </button>
          )}
          {canApplyDiscount &&
            ['CREATED', 'PENDING_APPROVAL', 'CONFIRMED'].includes(order.status) && (
              <button
                onClick={() => setShowDiscount((v) => !v)}
                className="bg-secondary text-secondary-foreground rounded px-3 py-1"
              >
                {t('orders.detail.applyDiscount')}
              </button>
            )}
          {order.status === 'PENDING_APPROVAL' && canApprove && (
            <button
              onClick={() => action('approve-credit')}
              className="bg-success text-primary-foreground rounded px-3 py-1"
            >
              {t('orders.detail.approveCredit')}
            </button>
          )}
          {order.status === 'PENDING_APPROVAL' && canApprove && (
            <button
              onClick={() => action('reject-credit')}
              className="bg-destructive text-primary-foreground rounded px-3 py-1"
            >
              {t('orders.detail.rejectCredit')}
            </button>
          )}
          {order.status === 'CREATED' && canConfirm && !order.supplierBlock && (
            <button
              onClick={() => action('confirm')}
              className="bg-primary text-primary-foreground rounded px-3 py-1"
            >
              {t('orders.detail.confirm')}
            </button>
          )}
          {/* PARTIALLY_DELIVERED goes back to the bench for the NEXT shipment (WP-3). */}
          {(order.status === 'CONFIRMED' || order.status === 'PARTIALLY_DELIVERED') &&
            canPrepare && (
              <button
                onClick={() => action('prepare')}
                className="bg-primary text-primary-foreground rounded px-3 py-1"
              >
                {order.status === 'PARTIALLY_DELIVERED'
                  ? t('orders.detail.prepareNext')
                  : t('orders.detail.startPreparation')}
              </button>
            )}
          {order.status === 'PREPARING' && canPrepare && (
            // INV-41 — lines are picked by scanning each unit, never in one click.
            <button
              onClick={() => navigate(`/warehouse/picking?orderId=${id}`)}
              className="bg-secondary text-secondary-foreground rounded px-3 py-1"
            >
              {t('orders.detail.scanToPick')}
            </button>
          )}
          {order.status === 'PREPARING' && canReady && (
            <button
              onClick={() => action('ready')}
              className="bg-primary text-primary-foreground rounded px-3 py-1"
            >
              {t('orders.detail.markReady')}
            </button>
          )}
          {/* SC-08 — RETURN_CREATE is open to any authenticated caller by design ("a customer or
              the counter may raise one; approval is the gate"), so the button follows the GOODS,
              not a permission: it appears exactly when this order still has delivered units that
              no return has claimed. */}
          {canReconcile &&
            quote?.onAccount &&
            quote.awaitedTTC > 0.005 &&
            Number(order.amountDue ?? 0) > 0.005 &&
            !['CREATED', 'PENDING_APPROVAL', 'FROZEN', 'CANCELLED'].includes(order.status) && (
              <button
                onClick={() => {
                  setPrepayAmount(
                    Math.min(quote.awaitedTTC, Number(order.amountDue ?? 0)).toFixed(2)
                  );
                  setPrepaying((v) => !v);
                }}
                className="rounded border px-3 py-1"
              >
                {t('orders.sheet.prepay.action')}
              </button>
            )}
          {returnableLines.length > 0 && (
            <button
              onClick={() => setReturning((v) => !v)}
              className="bg-secondary text-secondary-foreground rounded px-3 py-1"
            >
              {t('orders.detail.return.action')}
            </button>
          )}
          {/* ORDER_CANCEL (MGR/OWN) — before delivery it cancels the order; after a shipment it
              cancels the remaining lines and, on a prepaid order, mints the avoir. */}
          {CANCELLABLE.includes(order.status) &&
            canCancel &&
            !(order.lines ?? []).some((l) => (l.quantityDelivered ?? 0) > 0) && (
              <button
                onClick={() => setCancelling((v) => !v)}
                className="bg-destructive/10 text-destructive rounded px-3 py-1"
              >
                {t('common.cancel')}
              </button>
            )}
        </div>

        {/* Staff cancel with a reason (receiving spec §6) — the server refuses one without. */}
        {cancelling && (
          <div className="mt-3 border-t pt-3 space-y-2 max-w-2xl">
            <p className="text-xs text-muted-foreground">{t('orders.detail.cancelOrder.hint')}</p>
            <label className="block text-sm">
              <span className="block text-xs text-muted-foreground mb-1">
                {t('orders.detail.cancelOrder.reasonLabel')}
              </span>
              <input
                value={cancelReason}
                onChange={(e) => setCancelReason(e.target.value)}
                placeholder={t('orders.detail.cancelOrder.reasonPlaceholder')}
                className="border rounded px-2 py-1 w-full"
              />
            </label>
            <button
              onClick={async () => {
                setCancelBusy(true);
                await action('cancel', { reason: cancelReason.trim() });
                setCancelBusy(false);
                setCancelling(false);
                setCancelReason('');
              }}
              disabled={cancelBusy || cancelReason.trim().length < 10}
              className="bg-destructive text-destructive-foreground rounded px-3 py-1 disabled:opacity-50"
            >
              {t('orders.detail.cancelOrder.confirm')}
            </button>
          </div>
        )}

        {returning && returnableLines.length > 0 && (
          <div className="mt-3 border-t pt-3 space-y-3 max-w-2xl">
            <p className="text-xs text-muted-foreground">{t('orders.detail.return.hint')}</p>
            <div className="space-y-1">
              {returnableLines.map((r) => (
                <div key={r.productId} className="flex items-center gap-3 text-sm">
                  <span className="font-mono text-xs w-32 truncate" title={r.sku ?? ''}>
                    {r.sku}
                  </span>
                  <span className="flex-1 truncate" title={r.name ?? ''}>
                    {r.name}
                  </span>
                  <span className="text-xs text-muted-foreground whitespace-nowrap">
                    {t('orders.detail.return.returnable', {
                      max: r.max,
                      delivered: r.delivered,
                    })}
                  </span>
                  <input
                    type="number"
                    min={0}
                    max={r.max}
                    step={1}
                    value={returnQty[r.productId] ?? ''}
                    placeholder="0"
                    aria-label={t('orders.detail.return.qtyLabel', { sku: r.sku ?? '' })}
                    onChange={(e) => setReturnQty((q) => ({ ...q, [r.productId]: e.target.value }))}
                    className="border rounded px-2 py-1 w-20 text-right tabular-nums"
                  />
                </div>
              ))}
            </div>
            <label className="block text-sm">
              <span className="block text-xs text-muted-foreground mb-1">
                {t('orders.detail.return.reasonLabel')}
              </span>
              <input
                value={returnReason}
                onChange={(e) => setReturnReason(e.target.value)}
                placeholder={t('orders.detail.return.reasonPlaceholder')}
                className="border rounded px-2 py-1 w-full"
              />
            </label>
            <button
              onClick={() => void submitReturn()}
              disabled={returnBusy}
              className="bg-primary text-primary-foreground rounded px-3 py-1 disabled:opacity-50"
            >
              {t('orders.detail.return.confirm')}
            </button>
          </div>
        )}

        {prepaying && (
          <div className="mt-3 border-t pt-3 space-y-2 max-w-2xl">
            <p className="text-xs text-muted-foreground">{t('orders.sheet.prepay.hint')}</p>
            <div className="flex flex-wrap items-end gap-2 text-sm">
              <label className="flex flex-col gap-1">
                <span className="text-xs text-muted-foreground">
                  {t('orders.detail.payment.method')}
                </span>
                <Select
                  className="w-auto"
                  value={prepayMethod}
                  onChange={(e) => setPrepayMethod(e.target.value)}
                >
                  {['TRANSFER', 'CHECK', 'CASH', 'CARD'].map((m) => (
                    <option key={m} value={m}>
                      {enumLabel('paymentMethod', m)}
                    </option>
                  ))}
                </Select>
              </label>
              <label className="flex flex-col gap-1">
                <span className="text-xs text-muted-foreground">
                  {t('orders.detail.payment.amount')}
                </span>
                <Input
                  type="number"
                  min="0"
                  step="0.01"
                  className="w-32 tabular-nums"
                  value={prepayAmount}
                  onChange={(e) => setPrepayAmount(e.target.value)}
                />
              </label>
              <label className="flex flex-col gap-1">
                <span className="text-xs text-muted-foreground">
                  {t('orders.detail.payment.reference')}
                </span>
                <Input
                  className="w-48"
                  value={prepayRef}
                  onChange={(e) => setPrepayRef(e.target.value)}
                />
              </label>
              <button
                onClick={() => void submitPrepay()}
                disabled={prepayBusy}
                className="bg-primary text-primary-foreground rounded px-3 py-1.5 disabled:opacity-50"
              >
                {t('orders.sheet.prepay.confirm')}
              </button>
            </div>
          </div>
        )}

        {showDiscount && (
          <div className="mt-3 border-t pt-3 space-y-2 max-w-lg">
            <p className="text-xs text-muted-foreground">{t('orders.detail.discountHint')}</p>
            <div className="flex flex-wrap items-end gap-2">
              <label className="text-xs">
                <span className="block mb-1 text-muted-foreground">
                  {t('orders.detail.discountPct')}
                </span>
                <Input
                  type="number"
                  min={0}
                  max={100}
                  step={0.5}
                  value={discountPct}
                  onChange={(e) => setDiscountPct(e.target.value)}
                  className="w-24"
                  aria-label={t('orders.detail.discountPercent')}
                />
              </label>
              <label className="text-xs flex-1 min-w-[16rem]">
                <span className="block mb-1 text-muted-foreground">
                  {t('orders.detail.discountReasonLabel')}
                </span>
                <Input
                  value={discountReason}
                  onChange={(e) => setDiscountReason(e.target.value)}
                  placeholder={t('orders.detail.discountReasonPlaceholder')}
                  aria-label={t('orders.detail.discountReasonAria')}
                />
              </label>
              <button
                onClick={() => void applyDiscount()}
                disabled={applyingDiscount}
                className="bg-primary text-primary-foreground rounded px-3 py-1 disabled:opacity-50"
              >
                {applyingDiscount ? t('orders.detail.applying') : t('orders.detail.apply')}
              </button>
              <button onClick={() => setShowDiscount(false)} className="rounded px-3 py-1 border">
                {t('common.cancel')}
              </button>
            </div>
          </div>
        )}
      </section>
    </div>
  );
}

/** WP-11 — a bon de retrait: goods set aside for this customer, and the term they are held for. */
interface PickupSlipDto {
  id: string;
  slip_number: string;
  status: 'PENDING' | 'COLLECTED' | 'RESOLVED' | 'CANCELLED';
  deadline_at: string;
  outstanding_ttc: string;
  resolution: 'PREPAID_AVOIR' | 'CREDIT_RELEASE' | null;
  resolved_ttc: string | null;
}

/**
 * WP-11 — the pickup slip (bon de retrait) and its art. 1657 resolution.
 *
 * The resolution belongs to the SYSTEM job that runs at the term; this is where an operator can
 * ask for it once the term HAS run out (and see, before then, exactly when it will). The button
 * is deliberately still offered while the term runs — the project rule is that the endpoint is
 * the source of truth and hiding a control is not enforcement — but it carries the countdown,
 * and the server answers a premature click with PICKUP_BEFORE_DEADLINE for every role.
 */
interface ReceiptDto {
  id: string;
  receipt_number: string;
  kind: 'DEPOSIT' | 'SETTLEMENT';
  created_at: string;
  amount_received: string;
  goods_ttc: string;
  invoice_id: string | null;
  invoice_number: string | null;
  delivery_note_number: string | null;
}

/**
 * Receipts of a cash sale (migration 111): a DEPOSIT receipt for money taken before the goods
 * leave, a SETTLEMENT receipt per hand-over. The consumer's invoice exists (it is the accounting
 * record) but is handed over only when asked — hence "Issue invoice" next to each settlement.
 */
function Receipts({ orderId, refreshKey }: { orderId: string; refreshKey: number }) {
  const { t } = useTranslation();
  const { showApiError } = useToast();
  const [rows, setRows] = useState<ReceiptDto[]>([]);
  // Deposit invoices (DEP, migration 114) — tax documents for money paid before the hand-over.
  const [deposits, setDeposits] = useState<
    Array<{
      id: string;
      invoice_number: string;
      issued_at: string;
      total_ttc: string;
      deposit_percent: string | null;
    }>
  >([]);
  const [invoice, setInvoice] = useState<{ id: string; number: string } | null>(null);

  useEffect(() => {
    let alive = true;
    apiClient
      .get<{ receipts: ReceiptDto[]; depositInvoices: typeof deposits }>('/receipts', {
        params: { orderId },
      })
      .then((r) => {
        if (!alive) return;
        setRows(r.data?.receipts ?? []);
        setDeposits(r.data?.depositInvoices ?? []);
      })
      .catch(() => {
        /* a role without order read access simply sees no section */
      });
    return () => {
      alive = false;
    };
  }, [orderId, refreshKey]);

  const openPdf = async (r: ReceiptDto) => {
    try {
      const res = await apiClient.get(`/receipts/${r.id}/pdf`, { responseType: 'blob' });
      const url = URL.createObjectURL(res.data as Blob);
      window.open(url, '_blank', 'noopener');
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (err) {
      showApiError(err);
    }
  };

  if (rows.length === 0 && deposits.length === 0) return null;
  return (
    <section className="border rounded p-4 bg-card">
      <h2 className="font-semibold mb-2">{t('orders.detail.receipts.title')}</h2>
      <ul className="space-y-2 text-sm">
        {deposits.map((d) => (
          <li key={d.id} className="flex flex-wrap items-center gap-3">
            <FileText className="h-4 w-4 text-muted-foreground" />
            <span className="font-mono">{d.invoice_number}</span>
            <span className="text-xs px-1.5 py-0.5 rounded bg-warning/20">
              {t('orders.detail.receipts.depositInvoice')}
            </span>
            <span className="text-xs text-muted-foreground">
              {formatDate(d.issued_at)} · {formatEUR(Number(d.total_ttc))}
              {d.deposit_percent != null ? ` · ${Number(d.deposit_percent).toFixed(2)} %` : ''}
            </span>
            <div className="ml-auto">
              <button
                onClick={() => setInvoice({ id: d.id, number: d.invoice_number })}
                className="text-primary hover:underline"
              >
                {t('orders.detail.receipts.printDeposit')}
              </button>
            </div>
          </li>
        ))}
        {rows.map((r) => (
          <li key={r.id} className="flex flex-wrap items-center gap-3">
            <FileText className="h-4 w-4 text-muted-foreground" />
            <span className="font-mono">{r.receipt_number}</span>
            <span className="text-xs px-1.5 py-0.5 rounded bg-secondary text-secondary-foreground">
              {r.kind === 'DEPOSIT'
                ? t('orders.detail.receipts.deposit')
                : t('orders.detail.receipts.settlement')}
            </span>
            <span className="text-xs text-muted-foreground">
              {formatDate(r.created_at)} ·{' '}
              {t('orders.detail.receipts.received', {
                amount: formatEUR(Number(r.amount_received)),
              })}
              {r.delivery_note_number ? ` · ${r.delivery_note_number}` : ''}
            </span>
            <div className="ml-auto flex flex-wrap gap-3">
              <button onClick={() => void openPdf(r)} className="text-primary hover:underline">
                {t('common.view')}
              </button>
              {r.invoice_id && r.invoice_number && (
                <button
                  onClick={() => setInvoice({ id: r.invoice_id!, number: r.invoice_number! })}
                  className="text-primary hover:underline"
                >
                  {t('orders.detail.receipts.issueInvoice', { number: r.invoice_number })}
                </button>
              )}
            </div>
          </li>
        ))}
      </ul>
      {invoice && (
        <PrintDocumentDialog
          docType="INVOICE"
          documentId={invoice.id}
          documentNumber={invoice.number}
          orderId={orderId}
          onClose={() => setInvoice(null)}
        />
      )}
    </section>
  );
}

/**
 * The order's bin (migration 112): where its supplier parts are put once received. The order
 * number is the label until a bin is typed, so a received supplier line always has a place.
 * Saved on blur or Enter; an empty field clears it back to the order number.
 */
function OrderBinInput({
  orderId,
  orderNumber,
  bin,
  editable,
  onSaved,
}: {
  orderId: string;
  orderNumber: string;
  bin: string | null;
  editable: boolean;
  onSaved: () => void | Promise<void>;
}) {
  const { t } = useTranslation();
  const { showApiError, push } = useToast();
  const saved = bin ?? '';
  const [value, setValue] = useState(saved);
  const [busy, setBusy] = useState(false);
  useEffect(() => setValue(saved), [saved]);

  const save = async () => {
    const next = value.trim();
    if (next === saved || busy) return;
    setBusy(true);
    try {
      await apiClient.post(
        `/orders/${orderId}/bin`,
        { bin: next || null },
        { headers: { 'X-Command-Id': crypto.randomUUID() } }
      );
      push({ tone: 'success', message: t('orders.detail.binSaved') });
      await onSaved();
    } catch (err) {
      showApiError(err);
      setValue(saved);
    } finally {
      setBusy(false);
    }
  };

  return (
    <label className="flex items-center gap-2 text-xs text-muted-foreground">
      {t('orders.detail.orderBin')}
      {editable ? (
        <input
          value={value}
          disabled={busy}
          maxLength={40}
          placeholder={orderNumber}
          aria-label={t('orders.detail.orderBin')}
          onChange={(e) => setValue(e.target.value.toUpperCase())}
          onBlur={() => void save()}
          onKeyDown={(e) => {
            if (e.key === 'Enter') (e.target as HTMLInputElement).blur();
            if (e.key === 'Escape') setValue(saved);
          }}
          className="w-32 rounded border bg-background px-1.5 py-0.5 font-mono text-xs font-semibold text-foreground"
        />
      ) : (
        <span className="font-mono font-semibold text-foreground">{bin || orderNumber}</span>
      )}
    </label>
  );
}

function PickupSlips({
  orderId,
  refreshKey,
  canResolve,
  onResolved,
}: {
  orderId: string;
  refreshKey: number;
  canResolve: boolean;
  onResolved: () => void;
}) {
  const { t } = useTranslation();
  const { showApiError, push } = useToast();
  const [slips, setSlips] = useState<PickupSlipDto[]>([]);
  const [busy, setBusy] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    apiClient
      .get<PickupSlipDto[]>('/pickup-slips', { params: { orderId } })
      .then((r) => {
        if (alive) setSlips(r.data ?? []);
      })
      .catch(() => {
        /* a role without ORDER_VIEW simply sees no section */
      })
      .finally(() => {
        if (alive) setLoading(false);
      });
    return () => {
      alive = false;
    };
  }, [orderId, refreshKey]);

  const resolve = async (slip: PickupSlipDto) => {
    setBusy(slip.id);
    try {
      const { data } = await apiClient.post<{ resolution: string; resolvedTTC: number }>(
        `/pickup-slips/${slip.id}/resolve`
      );
      push({
        tone: 'success',
        message:
          data.resolution === 'PREPAID_AVOIR'
            ? t('orders.detail.pickup.resolvedAvoirToast')
            : t('orders.detail.pickup.resolvedCancelToast'),
      });
      onResolved();
    } catch (err) {
      showApiError(err);
    } finally {
      setBusy(null);
    }
  };

  if (loading || slips.length === 0) return null;

  return (
    <section className="border rounded p-4 bg-card">
      <h2 className="font-semibold mb-2">{t('orders.detail.pickup.title')}</h2>
      {/* 2026-10-08 — the BR is no longer a customer document: the pickup code and the parts to
          come are printed on the hand-over document itself (ticket, invoice or BL). This record
          keeps the 14-day deadline and its art. 1657 resolution. */}
      <p className="mb-2 text-xs text-muted-foreground">{t('orders.detail.pickup.onDocuments')}</p>
      <ul className="space-y-2 text-sm">
        {slips.map((s) => {
          const overdue = new Date(s.deadline_at).getTime() <= Date.now();
          return (
            <li key={s.id} className="flex flex-wrap items-center gap-3">
              <FileText className="h-4 w-4 text-muted-foreground" />
              <span className="font-mono">{s.slip_number}</span>
              <span className="text-xs px-1.5 py-0.5 rounded bg-secondary text-secondary-foreground">
                {enumLabel('pickupStatus', s.status)}
              </span>
              <span className={`text-xs ${overdue ? 'text-warning' : 'text-muted-foreground'}`}>
                {s.status === 'PENDING'
                  ? overdue
                    ? t('orders.detail.pickup.expired', { date: formatDate(s.deadline_at) })
                    : t('orders.detail.pickup.toCollect', { date: formatDate(s.deadline_at) })
                  : s.resolution === 'PREPAID_AVOIR'
                    ? t('orders.detail.pickup.resolvedAvoir', {
                        amount: formatEUR(s.resolved_ttc),
                      })
                    : s.resolution === 'CREDIT_RELEASE'
                      ? t('orders.detail.pickup.resolvedCancel')
                      : formatDate(s.deadline_at)}
              </span>
              <div className="ml-auto flex flex-wrap gap-3">
                {s.status === 'PENDING' && canResolve && (
                  <button
                    onClick={() => resolve(s)}
                    disabled={busy === s.id}
                    className="text-primary hover:underline disabled:opacity-50"
                    title={
                      overdue
                        ? t('orders.detail.pickup.resolveTitleReady')
                        : t('orders.detail.pickup.resolveTitleEarly')
                    }
                  >
                    {t('orders.detail.pickup.resolve')}
                  </button>
                )}
              </div>
            </li>
          );
        })}
      </ul>
    </section>
  );
}

/**
 * WP-2 — the payment instruments, and which of them can settle money AT THE COUNTER.
 *
 * A wire, a SEPA direct debit and an LCR all collect an ISSUED invoice on its due date; they
 * are not cash changing hands here. `POST /orders/:id/pay` refuses them with 422
 * SETTLEMENT_CHANNEL_NOT_AT_COUNTER.
 *
 * They are deliberately still OFFERED here rather than filtered out of the list: the project
 * rule is that the endpoint is the source of truth and hiding a control is not enforcement.
 * An operator who believes a wire settles this order gets the server's actual reason —
 * "record it against the FAC instead" — rather than a silently missing option.
 */
const PAYMENT_METHODS: { value: string; deferred?: boolean }[] = [
  { value: 'CASH' },
  { value: 'CARD' },
  { value: 'CHECK' },
  { value: 'TRANSFER', deferred: true },
  { value: 'SEPA', deferred: true },
  { value: 'LCR', deferred: true },
];

/** SC-04 step 8 — record what was collected at the counter against this order. */
function RecordPayment({
  orderId,
  amountDue,
  onRecorded,
}: {
  orderId: string;
  amountDue: number;
  onRecorded: () => Promise<void> | void;
}) {
  const { t } = useTranslation();
  const { push } = useToast();
  const [method, setMethod] = useState('CASH');
  const [amount, setAmount] = useState(amountDue > 0 ? amountDue.toFixed(2) : '');
  // Follow the balance: after a payment is recorded the field must not still hold the amount
  // just paid, or one more click pays it again.
  useEffect(() => {
    setAmount(amountDue > 0 ? amountDue.toFixed(2) : '');
  }, [amountDue]);
  const [reference, setReference] = useState('');
  const [busy, setBusy] = useState(false);
  // Rendered inline (not only as a toast) so the refusal stays on screen next to the control
  // that caused it.
  const [failure, setFailure] = useState<{ title: string; message: string } | null>(null);

  const selected = PAYMENT_METHODS.find((m) => m.value === method);

  const submit = async () => {
    const value = Number(amount);
    if (!Number.isFinite(value) || value <= 0) {
      push({ tone: 'warning', message: t('orders.detail.payment.amountPositive') });
      return;
    }
    setBusy(true);
    setFailure(null);
    try {
      const { data } = await apiClient.post<{
        depositInvoice?: { invoiceNumber: string } | null;
      }>(
        `/orders/${orderId}/pay`,
        {
          payments: [
            { method, amount: value, ...(reference.trim() ? { reference: reference.trim() } : {}) },
          ],
        },
        { headers: { 'X-Command-Id': crypto.randomUUID() } }
      );
      push({
        tone: 'success',
        message:
          t('orders.detail.payment.recorded', {
            amount: formatEUR(value),
            method: enumLabel('paymentMethod', method),
          }) +
          (data?.depositInvoice
            ? ' ' +
              t('orders.detail.receipts.depositIssued', {
                number: data.depositInvoice.invoiceNumber,
              })
            : ''),
      });
      setReference('');
      await onRecorded();
    } catch (err) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const resp = (err as any)?.response;
      setFailure(describeApiError(resp?.data?.code, resp?.data?.message, resp?.status));
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="border rounded p-4 bg-card">
      <div className="flex items-baseline justify-between gap-3 mb-2">
        <h2 className="font-semibold">{t('orders.detail.payment.title')}</h2>
        {amountDue > 0 && (
          <span className="text-sm text-muted-foreground tabular-nums">
            {t('orders.detail.payment.stillDue', { amount: formatEUR(amountDue) })}
          </span>
        )}
      </div>

      <div className="flex flex-wrap items-end gap-2 text-sm">
        <label className="flex flex-col gap-1">
          <span className="text-xs text-muted-foreground">{t('orders.detail.payment.method')}</span>
          <Select
            className="w-auto"
            value={method}
            onChange={(e) => {
              setMethod(e.target.value);
              setFailure(null);
            }}
          >
            {PAYMENT_METHODS.map((m) => (
              <option key={m.value} value={m.value}>
                {enumLabel('paymentMethod', m.value)}
                {m.deferred ? t('orders.detail.payment.settlesFac') : ''}
              </option>
            ))}
          </Select>
        </label>
        <label className="flex flex-col gap-1">
          <span className="text-xs text-muted-foreground">{t('orders.detail.payment.amount')}</span>
          <Input
            type="number"
            min="0"
            step="0.01"
            className="w-32 tabular-nums"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
          />
        </label>
        <label className="flex flex-col gap-1">
          <span className="text-xs text-muted-foreground">
            {t('orders.detail.payment.reference')}
          </span>
          <Input
            className="w-48"
            value={reference}
            onChange={(e) => setReference(e.target.value)}
            placeholder={t('orders.detail.payment.referencePlaceholder')}
          />
        </label>
        <button
          onClick={() => void submit()}
          disabled={busy}
          className="bg-primary text-primary-foreground rounded px-3 py-1.5 disabled:opacity-50"
        >
          {busy ? t('orders.detail.payment.recording') : t('orders.detail.payment.record')}
        </button>
      </div>

      {selected?.deferred && !failure && (
        <p className="mt-2 text-xs text-warning">
          {t('orders.detail.payment.deferredNote', {
            method: enumLabel('paymentMethod', selected.value),
          })}
        </p>
      )}

      {failure && (
        <div className="mt-3 rounded border border-destructive/40 bg-destructive/10 p-3">
          <div className="font-medium text-destructive text-sm">{failure.title}</div>
          <div className="text-sm text-muted-foreground mt-0.5">{failure.message}</div>
        </div>
      )}
    </section>
  );
}
